import React, { useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';

import FeatherIcon from '../common/FeatherIcon';
import './ModuleLocked.css';

/**
 * The small lock beside a nav entry (module, group or row) whose module the
 * org has not bought. The entry stays clickable: the page opens on the lock
 * screen, so a tenant can see what exists without seeing any data.
 *
 * Hovering the lock says why, naming the module when the caller knows it
 * (user, 2026-10-06). The tooltip is drawn in a portal so the sidebar's scroll
 * area cannot clip it, and never takes the pointer, so the row stays clickable.
 * Screen readers get the same reason through aria-description.
 */
export default function NavLock({ size = 12, className = '', module = '' }) {
  const { t } = useTranslation('moduleLicensing');
  const ref = useRef(null);
  const [pos, setPos] = useState(null);
  const label = t('moduleLicensing:nav.notInPlan', { defaultValue: 'Not included in your plan' });
  const why = module
    ? t('moduleLicensing:nav.lockedWhy', {
      module,
      defaultValue: "{{module}} isn't in your organization's plan. You can open its pages to see what it does; an administrator can add it.",
    })
    : t('moduleLicensing:nav.lockedWhyGeneric', {
      defaultValue: "This isn't in your organization's plan. You can open its pages to see what it does; an administrator can add it.",
    });

  const show = () => {
    const r = ref.current && ref.current.getBoundingClientRect();
    if (!r) return;
    const half = 130;
    const centre = Math.min(Math.max(r.left + r.width / 2, half + 8), window.innerWidth - half - 8);
    setPos({ top: r.bottom + 8, left: centre });
  };

  return (
    <span
      ref={ref}
      className={`nav-lock-icon ${className}`.trim()}
      role="img"
      aria-label={label}
      aria-description={why}
      data-testid="nav-lock"
      onMouseEnter={show}
      onMouseLeave={() => setPos(null)}
    >
      <FeatherIcon name="lock" size={size} aria-hidden="true" focusable="false" />
      {pos && ReactDOM.createPortal(
        <span className="nav-lock-tooltip" role="tooltip" style={{ top: pos.top, left: pos.left }}>
          <strong>{label}</strong>
          <span>{why}</span>
        </span>,
        document.body,
      )}
    </span>
  );
}
